import React from 'react';
import {
  SimpleGrid,
  Card,
  CardBody,
  Box,
  Text,
  HStack,
  VStack,
  Icon
} from '@chakra-ui/react';
import { MdSecurity, MdGavel, MdLocalParking, MdAttachMoney } from 'react-icons/md';
import { MultaPolicia } from '../../services/policiaMunicipalApi';

interface DashboardInfraccionesProps {
  infracciones: MultaPolicia[];
}

const DashboardInfracciones: React.FC<DashboardInfraccionesProps> = ({ infracciones }) => {
  // Calcular estadísticas
  const totalInfracciones = infracciones.length;
  const infraccionesPendientes = infracciones.filter(i => i.estatus === 'PENDIENTE').length;
  const infraccionesPagadas = infracciones.filter(i => i.estatus === 'PAGADA').length;
  const vehiculosAsegurados = infracciones.filter(i => i.tipoGarantia === 'vehiculo').length;
  
  const montoTotal = infracciones.reduce((sum, i) => sum + (Number(i.monto) || 0), 0);
  const montoPendiente = infracciones
    .filter(i => i.estatus === 'PENDIENTE')
    .reduce((sum, i) => sum + (Number(i.monto) || 0), 0);

  // Calcular porcentajes
  const porcentajePagadas = totalInfracciones > 0 ? (infraccionesPagadas / totalInfracciones) * 100 : 0;
  const porcentajeRecaudacion = montoTotal > 0 ? ((montoTotal - montoPendiente) / montoTotal) * 100 : 0;

  // Infracciones del mes actual
  const fechaActual = new Date();
  const mesActual = fechaActual.getMonth();
  const añoActual = fechaActual.getFullYear();
  
  const infraccionesEsteMes = infracciones.filter(i => {
    if (!i.fechaInfraccion) return false;
    const fechaInfraccion = new Date(i.fechaInfraccion);
    return fechaInfraccion.getMonth() === mesActual && fechaInfraccion.getFullYear() === añoActual;
  }).length;

  // Mes anterior para comparación
  const mesAnterior = mesActual === 0 ? 11 : mesActual - 1;
  const añoMesAnterior = mesActual === 0 ? añoActual - 1 : añoActual;
  
  const infraccionesMesAnterior = infracciones.filter(i => {
    if (!i.fechaInfraccion) return false;
    const fechaInfraccion = new Date(i.fechaInfraccion);
    return fechaInfraccion.getMonth() === mesAnterior && fechaInfraccion.getFullYear() === añoMesAnterior;
  }).length;

  const cambioMensual = infraccionesMesAnterior > 0 
    ? ((infraccionesEsteMes - infraccionesMesAnterior) / infraccionesMesAnterior) * 100 
    : 0;

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
      {/* Total de Infracciones */}
      <Card bg="white" shadow="md">
        <CardBody>
          <HStack>
            <Icon as={MdGavel} boxSize={8} color="#5b182b" />
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color="#5b182b">{totalInfracciones}</Text>
              <Text fontSize="sm" color="gray.600">Total Infracciones</Text>
              <Text fontSize="xs" color="gray.500">
                {cambioMensual >= 0 ? '↗' : '↘'} {Math.abs(cambioMensual).toFixed(1)}% vs mes anterior
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Infracciones Pendientes */}
      <Card bg="white" shadow="md">
        <CardBody>
          <HStack>
            <Icon as={MdSecurity} boxSize={8} color="#bb8c52" />
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color="#bb8c52">{infraccionesPendientes}</Text>
              <Text fontSize="sm" color="gray.600">Pendientes</Text>
              <Text fontSize="xs" color="gray.500">
                {((infraccionesPendientes / totalInfracciones) * 100 || 0).toFixed(1)}% del total
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Vehículos Asegurados */}
      <Card bg="white" shadow="md">
        <CardBody>
          <HStack>
            <Icon as={MdLocalParking} boxSize={8} color="#e53e3e" />
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="bold" color="#e53e3e">{vehiculosAsegurados}</Text>
              <Text fontSize="sm" color="gray.600">Vehículos Asegurados</Text>
              <Text fontSize="xs" color="gray.500">
                En corralones municipales
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Recaudación */}
      <Card bg="white" shadow="md">
        <CardBody>
          <HStack>
            <Icon as={MdAttachMoney} boxSize={8} color="#48bb78" />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold" color="#48bb78">
                ${(montoTotal - montoPendiente).toLocaleString('es-MX')}
              </Text>
              <Text fontSize="sm" color="gray.600">Recaudación</Text>
              <Text fontSize="xs" color="gray.500">
                {porcentajeRecaudacion.toFixed(1)}% del total
              </Text>
            </VStack>
          </HStack>
        </CardBody>
      </Card>
    </SimpleGrid>
  );
};

export default DashboardInfracciones;